/*
 * Accessibility Widget – floating person icon
 * Controls: text size, high contrast, highlight links
 * Botanical Wellness theme – forest green
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Accessibility, ZoomIn, ZoomOut, Contrast, Link2, RotateCcw, X } from "lucide-react";

interface AccessibilityState {
  fontSize: number; // 0 = normal, 1 = large, 2 = x-large
  highContrast: boolean;
  highlightLinks: boolean;
}

const DEFAULT_STATE: AccessibilityState = {
  fontSize: 0,
  highContrast: false,
  highlightLinks: false,
};

const FONT_SIZES = ["100%", "115%", "130%"];
const FONT_LABELS = ["רגיל", "גדול", "גדול מאוד"];

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<AccessibilityState>(DEFAULT_STATE);

  // Apply accessibility settings to document
  useEffect(() => {
    const html = document.documentElement;

    // Font size
    html.style.fontSize = FONT_SIZES[state.fontSize];

    // High contrast
    if (state.highContrast) {
      html.classList.add("high-contrast");
    } else {
      html.classList.remove("high-contrast");
    }

    // Highlight links
    if (state.highlightLinks) {
      html.classList.add("highlight-links");
    } else {
      html.classList.remove("highlight-links");
    }
  }, [state]);

  const increaseFontSize = () =>
    setState((s) => ({ ...s, fontSize: Math.min(s.fontSize + 1, 2) }));

  const decreaseFontSize = () =>
    setState((s) => ({ ...s, fontSize: Math.max(s.fontSize - 1, 0) }));

  const toggleContrast = () =>
    setState((s) => ({ ...s, highContrast: !s.highContrast }));

  const toggleLinks = () =>
    setState((s) => ({ ...s, highlightLinks: !s.highlightLinks }));

  const resetAll = () => {
    setState(DEFAULT_STATE);
    document.documentElement.style.fontSize = "";
    document.documentElement.classList.remove("high-contrast", "highlight-links");
  };

  return (
    <>
      {/* Inject accessibility CSS */}
      <style>{`
        html.high-contrast {
          filter: contrast(1.5) brightness(1.05);
        }
        html.highlight-links a {
          text-decoration: underline !important;
          text-underline-offset: 3px !important;
          outline: 2px solid #E8A87C !important;
          outline-offset: 2px !important;
          border-radius: 2px !important;
        }
      `}</style>

      {/* Floating accessibility button – bottom right */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        {/* Panel */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="bg-white rounded-2xl shadow-2xl border border-[#2D5A3D]/10 p-5 w-64"
              style={{ direction: "rtl" }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3
                  className="text-[#1B4332] font-bold text-base"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  נגישות
                </h3>
                <button
                  onClick={() => setOpen(false)}
                  className="text-[#2D5A3D]/40 hover:text-[#2D5A3D] transition-colors"
                  aria-label="סגור תפריט נגישות"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Font size control */}
              <div className="mb-4">
                <p
                  className="text-[#3D6B4F] text-xs font-semibold mb-2 uppercase tracking-wide"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  גודל טקסט – {FONT_LABELS[state.fontSize]}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={decreaseFontSize}
                    disabled={state.fontSize === 0}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#F5F0E8] hover:bg-[#E8A87C]/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-[#2D5A3D] text-sm font-medium"
                    style={{ fontFamily: "var(--font-body)" }}
                    aria-label="הקטן טקסט"
                  >
                    <ZoomOut className="w-4 h-4" />
                    הקטן
                  </button>
                  <button
                    onClick={increaseFontSize}
                    disabled={state.fontSize === 2}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#F5F0E8] hover:bg-[#E8A87C]/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-[#2D5A3D] text-sm font-medium"
                    style={{ fontFamily: "var(--font-body)" }}
                    aria-label="הגדל טקסט"
                  >
                    <ZoomIn className="w-4 h-4" />
                    הגדל
                  </button>
                </div>
              </div>

              {/* High contrast toggle */}
              <button
                onClick={toggleContrast}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl mb-3 transition-all duration-200 ${
                  state.highContrast
                    ? "bg-[#1B4332] text-white"
                    : "bg-[#F5F0E8] text-[#2D5A3D] hover:bg-[#E8A87C]/20"
                }`}
                style={{ fontFamily: "var(--font-body)" }}
                aria-pressed={state.highContrast}
              >
                <span className="text-sm font-medium">ניגודיות גבוהה</span>
                <Contrast className="w-4 h-4" />
              </button>

              {/* Highlight links toggle */}
              <button
                onClick={toggleLinks}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl mb-4 transition-all duration-200 ${
                  state.highlightLinks
                    ? "bg-[#1B4332] text-white"
                    : "bg-[#F5F0E8] text-[#2D5A3D] hover:bg-[#E8A87C]/20"
                }`}
                style={{ fontFamily: "var(--font-body)" }}
                aria-pressed={state.highlightLinks}
              >
                <span className="text-sm font-medium">הדגשת קישורים</span>
                <Link2 className="w-4 h-4" />
              </button>

              {/* Reset */}
              <button
                onClick={resetAll}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#2D5A3D]/20 text-[#2D5A3D]/60 hover:text-[#2D5A3D] hover:border-[#2D5A3D]/40 transition-colors text-sm"
                style={{ fontFamily: "var(--font-body)" }}
              >
                <RotateCcw className="w-3.5 h-3.5" />
                איפוס הגדרות
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Toggle button */}
        <motion.button
          onClick={() => setOpen((o) => !o)}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
          className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#2D5A3D]/40 ${
            open
              ? "bg-[#1B4332] text-white"
              : "bg-[#2D5A3D] text-white hover:bg-[#1B4332]"
          }`}
          aria-label="פתח תפריט נגישות"
          aria-expanded={open}
        >
          <Accessibility className="w-7 h-7" />
        </motion.button>
      </div>
    </>
  );
}
